import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Loader2, Trash2, ChevronDown, Inbox, FileText } from "lucide-react";

import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pm1_admin_token";
const STATUS_OPTIONS = ["new", "contacted", "walkthrough", "quoted", "won", "lost"];

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const fieldBase =
  "w-full border border-border bg-white px-4 py-3 text-sm text-navy focus:border-brand-green focus:outline-none";

/* ------------------------------------------------------------------ LOGIN */
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-border p-8">
        <span className="flex h-12 w-12 items-center justify-center bg-navy text-brand-green">
          <Lock size={22} />
        </span>
        <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-navy">Admin Sign In</h1>
        <p className="mt-2 text-sm text-steel">PM1 Clean submissions dashboard.</p>
        <label className="mt-6 block">
          <span className="mb-2 block font-display text-xs font-semibold uppercase tracking-wider text-navy">Email</span>
          <input data-testid="admin-email" type="email" className={fieldBase} value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="mt-4 block">
          <span className="mb-2 block font-display text-xs font-semibold uppercase tracking-wider text-navy">Password</span>
          <input data-testid="admin-password" type="password" className={fieldBase} value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-destructive">{error}</p>}
        <button data-testid="admin-login-submit" type="submit" disabled={loading} className="btn-primary mt-6 w-full justify-center disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
};

/* -------------------------------------------------------------- QUOTE ROW */
const QuoteRow = ({ q, onStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  const rows = [
    ["Job Title", q.job_title],
    ["Phone", q.phone],
    ["Facility Address", q.facility_address],
    ["Service Area", q.service_area],
    ["Facility Type", q.facility_type],
    ["Square Footage", q.square_footage],
    ["Restroom Count", q.restroom_count],
    ["Floor Types", (q.floor_types || []).join(", ")],
    ["Frequency", q.frequency],
    ["Cleaning Challenges", q.cleaning_challenges],
    ["Special Instructions", q.special_instructions],
    ["Preferred Walkthrough", q.walkthrough_datetime],
  ];
  return (
    <div data-testid={`quote-row-${q.id}`} className="border border-border bg-white">
      <div className="flex flex-wrap items-center gap-4 p-4">
        <button onClick={() => setOpen((v) => !v)} className="flex flex-1 items-center gap-3 text-left">
          <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          <div>
            <p className="font-display font-bold text-navy">{q.company}</p>
            <p className="text-xs text-steel">{q.name} · {q.email}</p>
          </div>
        </button>
        <span className="text-xs text-steel">{new Date(q.created_at).toLocaleDateString()}</span>
        <select
          data-testid={`quote-status-${q.id}`}
          value={q.status || "new"}
          onChange={(e) => onStatus(q.id, e.target.value)}
          className="border border-border bg-cool-gray px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-navy focus:outline-none"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button data-testid={`quote-delete-${q.id}`} onClick={() => onDelete(q.id)} className="text-steel hover:text-destructive" aria-label="Delete">
          <Trash2 size={16} />
        </button>
      </div>
      {open && (
        <div className="grid gap-x-8 gap-y-2 border-t border-border bg-cool-gray/50 p-4 sm:grid-cols-2">
          {rows.map(([label, val]) => (
            <div key={label} className="flex gap-2 text-sm">
              <span className="min-w-[140px] font-semibold uppercase tracking-wide text-[11px] text-steel">{label}</span>
              <span className="text-navy">{val || "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------ DASHBOARD */
const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers: authHeader() }),
        axios.get(`${API}/admin/contacts`, { headers: authHeader() }),
      ]);
      setQuotes(q.data);
      setContacts(c.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await axios.patch(`${API}/admin/quotes/${id}/status`, { status }, { headers: authHeader() });
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const deleteItem = async (kind, id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/${kind}/${id}`, { headers: authHeader() });
      if (kind === "quotes") setQuotes((p) => p.filter((x) => x.id !== id));
      else setContacts((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="container-px mx-auto max-w-[1200px] py-12">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-navy">Submissions</h1>
          <p className="mt-1 text-sm text-steel">Quote requests & contact messages.</p>
        </div>
        <button data-testid="admin-logout" onClick={onLogout} className="btn-ghost">
          <LogOut size={16} /> Sign Out
        </button>
      </div>

      <div className="mt-8 flex gap-2 border-b border-border">
        <button
          data-testid="tab-quotes"
          onClick={() => setTab("quotes")}
          className={`flex items-center gap-2 px-4 py-3 font-display text-sm font-semibold uppercase tracking-wide ${tab === "quotes" ? "border-b-2 border-brand-green text-navy" : "text-steel"}`}
        >
          <FileText size={15} /> Quotes ({quotes.length})
        </button>
        <button
          data-testid="tab-messages"
          onClick={() => setTab("messages")}
          className={`flex items-center gap-2 px-4 py-3 font-display text-sm font-semibold uppercase tracking-wide ${tab === "messages" ? "border-b-2 border-brand-green text-navy" : "text-steel"}`}
        >
          <Inbox size={15} /> Messages ({contacts.length})
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-green-ink" /></div>
      ) : tab === "quotes" ? (
        <div className="mt-6 flex flex-col gap-3">
          {quotes.length === 0 && <p className="py-12 text-center text-steel">No quote requests yet.</p>}
          {quotes.map((q) => (
            <QuoteRow key={q.id} q={q} onStatus={updateStatus} onDelete={(id) => deleteItem("quotes", id)} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-3">
          {contacts.length === 0 && <p className="py-12 text-center text-steel">No messages yet.</p>}
          {contacts.map((c) => (
            <div key={c.id} data-testid={`contact-row-${c.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold text-navy">{c.subject || "(No subject)"}</p>
                  <p className="text-xs text-steel">{c.name} · {c.email}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-steel">{new Date(c.created_at).toLocaleDateString()}</span>
                  <button data-testid={`contact-delete-${c.id}`} onClick={() => deleteItem("contacts", c.id)} className="text-steel hover:text-destructive" aria-label="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="mt-3 border-t border-border pt-3 text-sm leading-relaxed text-navy">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return (
    <>
      <Seo title="Admin" description="PM1 Clean admin dashboard." path="/admin" />
      {authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />}
    </>
  );
}
